// ═══════════════════════════════════════════════════════════════════════════
// PAGE: MATRICE D'ACCÈS PROJETS × UTILISATEURS (lecture seule)
//
// Colonnes = projets (code, survol → nom), lignes = utilisateurs (trigramme +
// droit objet, survol → nom). 1ʳᵉ colonne et ligne d'en-tête figées (sticky).
// Cellule : ✓ vert = voit toutes les actions du projet ; ◑ orange = voit
// seulement certaines actions (DC) ; ✕ rouge = aucune action visible.
//
// Permission users.write (gérée côté router/backend). Données : object_rights
// (cf. backend access_matrix). Vue de supervision, sans édition.
// ═══════════════════════════════════════════════════════════════════════════

interface MatrixUser {
  id: number;
  trigram: string | null;
  name: string;
  object_rights: string | null;
}

interface MatrixProject {
  id: number;
  code: string;
  name: string;
}

interface AccessMatrixResp {
  users: MatrixUser[];
  projects: MatrixProject[];
  cells: Record<string, Record<string, string>>;
}

function _accessCellMark(status: string | undefined): string {
  if (status === 'full') {
    return '<span title="Voit toutes les actions" style="color:#147A57;font-weight:700">✓</span>';
  }
  if (status === 'partial') {
    return '<span title="Voit seulement certaines actions" style="color:#b45309;font-weight:700">◑</span>';
  }
  return '<span title="Aucune action visible" style="color:#dc2626;font-weight:700">✕</span>';
}

function _objRightBadge(right: string | null): string {
  const r = (right || '').toUpperCase();
  return `<span style="font-size:10px;padding:1px 5px;border-radius:6px;background:var(--bg3);color:var(--text2);margin-left:4px">${esc(r || '—')}</span>`;
}

pages.adminAccessMatrix = async function () {
  setHeader("Matrice d'accès projets × utilisateurs");
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);

  let data: AccessMatrixResp;
  try {
    data = (await api.getAccessMatrix()) as AccessMatrixResp;
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('common.error');
    setContent(`<div class="error">${esc(msg)}</div>`);
    return;
  }

  const users = data.users || [];
  const projects = data.projects || [];
  const cells = data.cells || {};

  if (!users.length || !projects.length) {
    setContent(
      `<div style="padding:24px;color:var(--text3)">Aucune donnée à afficher (aucun utilisateur ou aucun projet).</div>`,
    );
    return;
  }

  const legend = `<div style="margin:0 0 12px;font-size:13px;color:var(--text2)">
    <span style="color:#147A57;font-weight:700">✓</span>&nbsp;voit toutes les actions&nbsp;·
    <span style="color:#b45309;font-weight:700">◑</span>&nbsp;voit certaines actions&nbsp;·
    <span style="color:#dc2626;font-weight:700">✕</span>&nbsp;aucune action visible
  </div>`;

  const stickyBg = 'background:var(--bg,#fff)';

  const headCells = projects
    .map(
      (p) =>
        `<th title="${esc(p.name)}" style="position:sticky;top:0;z-index:4;${stickyBg};padding:6px 8px;border-bottom:1px solid var(--border);white-space:nowrap;font-size:12px;cursor:default">${esc(p.code)}</th>`,
    )
    .join('');

  const rows = users
    .map((u) => {
      const row = cells[String(u.id)] || {};
      const tds = projects
        .map(
          (p) =>
            `<td style="text-align:center;padding:4px 8px;border-bottom:1px solid var(--border)">${_accessCellMark(row[String(p.id)])}</td>`,
        )
        .join('');
      return `<tr>
        <th title="${esc(u.name)}" style="position:sticky;left:0;z-index:3;${stickyBg};text-align:left;padding:4px 10px;border-bottom:1px solid var(--border);border-right:1px solid var(--border);white-space:nowrap;cursor:default">
          <strong>${esc(u.trigram || '—')}</strong>${_objRightBadge(u.object_rights)}
        </th>${tds}
      </tr>`;
    })
    .join('');

  setContent(`
    ${legend}
    <div style="overflow:auto;max-height:calc(100vh - 200px);border:1px solid var(--border);border-radius:var(--radius)">
      <table style="border-collapse:separate;border-spacing:0;min-width:max-content">
        <thead>
          <tr>
            <th style="position:sticky;left:0;top:0;z-index:5;${stickyBg};text-align:left;padding:6px 10px;border-bottom:1px solid var(--border);border-right:1px solid var(--border)">Utilisateur</th>
            ${headCells}
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    </div>
  `);
};
